import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, LayoutDashboard, Layers, Gem, DollarSign, PieChart, TrendingUp,
  ChevronDown, BookOpen, Calculator,
  AlertTriangle, Lightbulb, HelpCircle, ArrowRight, Sparkles, MessageSquareQuote, Megaphone,
  Search, Lock
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────

interface HelpGuidePageProps {
  onClose: () => void;
}

type GuideTab = 'overview' | 'concepts' | 'tabs' | 'methodology' | 'reference';

interface TabGuideEntry {
  icon: typeof LayoutDashboard;
  name: string;
  subtitle?: string;
  color: string;
  question: string;
  overview: string;
  groups: { label: string; items: { name: string; desc: string }[] }[];
  tips?: string[];
}

// ─── Navigation ────────────────────────────────────────────────

const GUIDE_NAV: { id: GuideTab; label: string; shortLabel: string; icon: typeof BookOpen }[] = [
  { id: 'overview', label: 'Overview', shortLabel: 'Overview', icon: Lightbulb },
  { id: 'concepts', label: 'Key Concepts', shortLabel: 'Concepts', icon: Calculator },
  { id: 'tabs', label: 'Tab Guide', shortLabel: 'Tabs', icon: LayoutDashboard },
  { id: 'methodology', label: 'Methodology', shortLabel: 'Methods', icon: BookOpen },
  { id: 'reference', label: 'Reference', shortLabel: 'Reference', icon: Search },
];

// ─── App Tab Overview Cards ────────────────────────────────────

const APP_TABS = [
  { icon: LayoutDashboard, name: 'Market', color: '#0D9488', question: 'Is my portfolio profitable?', desc: 'Portfolio P&L, key metrics, recommendations, risks, and Export Summary for board decks.' },
  { icon: Layers, name: 'Packages', color: '#2563EB', question: 'What should I sell?', desc: 'Configure 1-5 packages with S1 offers, features, cost structures, and portfolio P&L.' },
  { icon: Gem, name: 'Feature Value', color: '#E11D48', question: 'Which features are worth it?', desc: 'Feature lift rankings, WTP values, bundle optimizer (128 combos), and marginal P&L.' },
  { icon: DollarSign, name: 'Pricing', color: '#059669', question: 'Where are the demand cliffs?', desc: 'Price elasticity curves, revenue-per-prospect analysis, and revenue waterfall.' },
  { icon: PieChart, name: 'Segments', color: '#7C3AED', question: 'Who are my customers?', desc: 'Three buyer segments with demographics, attitudes, brand consideration, and cross-tab filters.' },
  { icon: MessageSquareQuote, name: 'Voice of Market', color: '#0891B2', question: 'What do they actually say?', desc: 'Open-ended responses linked to conjoint behavior. Said vs. Did reveals hidden gems.' },
  { icon: TrendingUp, name: 'Financials', color: '#D97706', question: 'What do the economics look like?', desc: '12-month projection, LTV calculator, sensitivity heatmap, breakeven analysis.' },
  { icon: Megaphone, name: 'Marketing', color: '#DC2626', question: 'How should I market it?', desc: 'Market sizing, audience personas, AI messaging frameworks, strategic directions, ad previews.' },
];

// ─── Tab Guide Data ────────────────────────────────────────────

const TAB_GUIDE: TabGuideEntry[] = [
  {
    icon: LayoutDashboard, name: 'Market', subtitle: 'Executive Summary', color: '#0D9488',
    question: 'Is my portfolio profitable?',
    overview: 'A board-ready overview of your current portfolio\'s performance and the conjoint study\'s key findings.',
    groups: [{
      label: 'Sections',
      items: [
        { name: 'Strategic Recommendation', desc: 'Green banner if profitable, red if not. Shows monthly/annual P&L and units needed for breakeven.' },
        { name: 'Key Metrics Row', desc: 'Portfolio P&L, Total S2 Units, Contribution Margin %, and S1 Investment ROI at a glance.' },
        { name: 'Portfolio vs. Best Single', desc: 'Side-by-side comparison of your configured portfolio against the optimal standalone configuration.' },
        { name: 'Market at a Glance', desc: 'Three customer segments with their size, characterization, and key behaviors.' },
        { name: 'Headline Findings', desc: '#1 demand driver, revenue-maximizing price, addressable market, and demand spread.' },
        { name: 'Payment Terms Insight', desc: 'Actionable callout about payment structure impact on demand.' },
        { name: 'Key Risks', desc: 'HIGH-risk assumption gaps between the ProForma and conjoint evidence.' },
      ],
    }],
    tips: ['Use the Export Summary button to generate a printable executive summary with portfolio overview, package table, risks, and assumptions.'],
  },
  {
    icon: Layers, name: 'Package Builder', color: '#2563EB',
    question: 'What should I sell?',
    overview: 'Configure 1-5 packages with full portfolio economics. Each package is a separate product tier with its own S1 offer, price, features, and cost structure.',
    groups: [
      {
        label: 'Each Package Card',
        items: [
          { name: 'S1 \u2192 S2 Funnel', desc: 'Named S1 offer (e.g., "DNA Kit") with cost, linked to the S2 price.' },
          { name: 'S2 Price & Payment', desc: 'Select from 5 tested price points ($499\u2013$1,899) and 3 payment structures.' },
          { name: 'Feature Toggles', desc: 'Each feature shows its per-unit COGS so you see cost-value tradeoffs in real time.' },
          { name: 'Cost Parameters', desc: 'Per-package overrides for S1 cost, close rate, deductions, rep cost. Amber = custom.' },
          { name: 'Demand Metrics', desc: 'Purchase probability, required close rate (vs. 30% proven), S2 units (scenario).' },
          { name: 'Contribution', desc: 'Contribution margin, net revenue, net income per unit. No fixed costs at package level.' },
        ],
      },
      {
        label: 'Below the Cards',
        items: [
          { name: 'Suggest Tiers', desc: 'Auto-generates optimal feature bundles for your current package count.' },
          { name: 'Scenarios', desc: 'Save, name, and load full portfolio configurations for A/B comparison.' },
          { name: 'Portfolio P&L Summary', desc: 'Combined P&L with contribution waterfall, cannibalization slider, and CFO metrics.' },
          { name: 'Head-to-Head Comparison', desc: 'All packages side-by-side with a Portfolio Total column and "Best" winner.' },
          { name: 'Combined Market Reach', desc: 'Ceiling and split demand estimates with per-segment breakdown.' },
        ],
      },
    ],
    tips: ['Click "+" to add packages (max 5). Click X to remove (min 1). Save packages for the Marketing tab.'],
  },
  {
    icon: Gem, name: 'Feature Value', color: '#E11D48',
    question: 'Which features are worth including?',
    overview: 'How much each feature contributes to demand, in both percentage lift and implied dollar value (WTP).',
    groups: [{
      label: 'Analysis Sections',
      items: [
        { name: 'Feature Lift Ranking', desc: 'Raw demand lift when each feature is included vs. excluded. Smart Biometrics Ring leads at +13.2%.' },
        { name: 'WTP by Feature', desc: 'Translates lifts into dollar values using the demand-equivalence method.' },
        { name: 'Marginal Value Chart', desc: 'Scenario P&L impact of toggling each feature in the first package\'s current bundle.' },
        { name: 'Bundle Optimizer', desc: 'All 128 ON/OFF combinations ranked by scenario P&L. Your current bundle is highlighted.' },
      ],
    }],
    tips: ['Features with high lift AND high WTP belong in the premium tier. Low-lift features may not justify their variable cost.'],
  },
  {
    icon: DollarSign, name: 'Pricing', color: '#059669',
    question: 'Where are the demand cliffs?',
    overview: 'Price elasticity, demand curves, and the revenue waterfall at each tested price point.',
    groups: [{
      label: 'Analysis Sections',
      items: [
        { name: 'Elasticity Curve', desc: 'Choice rate and revenue per prospect on dual axes. Revenue-per-prospect peak at $1,499.' },
        { name: 'Revenue Waterfall', desc: 'Single mode (all features, full fixed costs) or Portfolio mode (per-package revenue, combined).' },
        { name: 'Elasticity Data Table', desc: 'Detailed data at every price: showings, selections, rate, revenue/prospect, cumulative.' },
      ],
    }],
    tips: ['Watch the demand drop between $499\u2013$999 (massive) vs. $1,499\u2013$1,899 (modest). This is the key insight for tier pricing.'],
  },
  {
    icon: PieChart, name: 'Segments', color: '#7C3AED',
    question: 'Who are my customers?',
    overview: 'Three market segments, their demographics, and how each responds to your configured packages.',
    groups: [{
      label: 'Visualization Sections',
      items: [
        { name: 'Segment Overview Cards', desc: 'Size, characterization, and demand at the selected price (changes with dropdown).' },
        { name: 'Demographic Profiles', desc: 'Per-segment demographics, attitudes, purchase factors, brand consideration, wearables.' },
        { name: 'Package Segment Demand', desc: 'Matrix showing how each configured package performs within each segment.' },
        { name: 'Segment Price Curves', desc: 'Overlaid demand curves with a reference line at the selected price.' },
        { name: 'Demographic Cross-Tab', desc: 'Filter by gender, age, income, region, health goal, spending, wearable ownership.' },
        { name: 'Bimodal Visualization', desc: 'Stacked bars showing the two-market structure with selected price highlighted.' },
      ],
    }],
    tips: ['The Selected Price dropdown affects overview cards, price curves, demand table, and bimodal chart across the entire tab.'],
  },
  {
    icon: MessageSquareQuote, name: 'Voice of Market', color: '#0891B2',
    question: 'What do respondents actually say?',
    overview: 'Open-ended survey responses from 1,000 respondents, linked to actual choice behavior and demographics.',
    groups: [
      {
        label: 'Analysis Sections',
        items: [
          { name: 'Said vs. Did', desc: 'Stated importance vs. revealed choices. "Hidden Gem" = underrated. "Overhyped" = overrated.' },
          { name: 'Spotlight Themes', desc: 'AI-identified themes across responses with behavioral context and representative quotes.' },
          { name: 'Verbatim Cards', desc: 'Per-respondent segment, goals, WTP factors, price reactions, and brand perception.' },
        ],
      },
      {
        label: 'Filters',
        items: [
          { name: 'Behavioral', desc: 'Segment, price preference range, price objection level.' },
          { name: 'Demographic', desc: 'Age range, health goal, monthly spending level.' },
        ],
      },
    ],
    tips: ['"Hidden Gem" features perform better in choices than people say \u2014 leverage these in marketing messaging.'],
  },
  {
    icon: TrendingUp, name: 'Financials', color: '#D97706',
    question: 'What do the economics look like?',
    overview: 'Financial projections with a toggle between Portfolio Analysis and Single-Product Analysis.',
    groups: [
      {
        label: 'Portfolio Analysis Mode',
        items: [
          { name: 'Portfolio P&L Summary', desc: 'Combined P&L with cannibalization slider and CFO metrics.' },
          { name: '12-Month Projection', desc: 'Cumulative and monthly P&L with adjustable S1 lead growth. Shows breakeven month.' },
          { name: 'Customer LTV', desc: 'Per-package lifetime value with retention, recurring revenue, and upsell inputs.' },
          { name: 'Portfolio Sensitivity', desc: 'P&L at different S1 lead levels using current package configurations.' },
          { name: 'Portfolio Breakeven', desc: 'Per-package contribution bars vs. fixed cost line.' },
        ],
      },
      {
        label: 'Single-Product Mode',
        items: [
          { name: 'Profitability Matrix', desc: 'Price \u00d7 S1 Leads heatmap with full fixed costs per price point.' },
          { name: 'Breakeven Analysis', desc: 'S1 leads needed to break even at each price (all features, full fixed costs).' },
        ],
      },
    ],
    tips: ['Both modes show the Assumption Gap Table and Key Risks. Use Portfolio mode for multi-tier strategy evaluation.'],
  },
  {
    icon: Megaphone, name: 'Marketing Strategy', color: '#DC2626',
    question: 'How should I market each package?',
    overview: 'Data-driven marketing strategy for saved packages. Combines conjoint data, buyer personas, and AI-generated frameworks.',
    groups: [{
      label: 'Workflow',
      items: [
        { name: '1. Save a Package', desc: 'In the Package Builder, click "Save" on any card to make it available here.' },
        { name: '2. Select Package', desc: 'Choose a saved package in the Marketing tab to work with.' },
        { name: '3. Market Sizing', desc: 'Total addressable market, gross revenue potential, and gender split.' },
        { name: '4. Audience Persona', desc: 'Demographics, psychographics, and conjoint behavior for the target buyer.' },
        { name: '5. Strategic Directions', desc: 'AI generates 3 positioning directions (Safe / Moderate / Bold).' },
        { name: '6. Messaging Framework', desc: 'Value proposition, message house, and StoryBrand BrandScript.' },
        { name: '7. Ad Creative Previews', desc: 'AI-generated mockups in social feed, story, and banner formats.' },
      ],
    }],
    tips: ['Save packages from the builder first, then generate strategy. Each direction includes tone, lead pillar, emotional trigger, and target audience.'],
  },
];

// ─── Glossary Data ─────────────────────────────────────────────

const GLOSSARY = [
  { term: 'Adjusted Choice Rate', aka: 'Purchase Probability', definition: 'The conjoint-derived demand rate for a configured package. Base rate at the selected price, adjusted upward by enabled feature lifts and dampened by the Feature Scaling parameter.' },
  { term: 'Cannibalization Rate', definition: 'The estimated percentage of higher-priced package demand that trades down to cheaper packages when multiple tiers are offered. Applied in split demand mode. Default: 15%.' },
  { term: 'Ceiling Demand', definition: 'Optimistic demand estimate where each package captures its full conjoint demand independently, assuming no cannibalization between tiers.' },
  { term: 'Choice Rate', aka: 'Concept-Level', definition: 'The probability that a survey respondent would choose a product concept at a given price, including none-choosers in the denominator.' },
  { term: 'Conditional Share', definition: 'The share of demand among respondents who chose ANY product (excludes none-choosers). Higher than concept-level rates.' },
  { term: 'Contribution Margin', definition: 'Net revenue minus all variable costs (S1 acquisition, S2 rep, feature COGS) for a single package. Fixed costs are NOT included \u2014 they are applied once at the portfolio level.' },
  { term: 'Customer LTV', definition: 'Customer Lifetime Value over 24 months, factoring in retention, recurring revenue, and upsell potential. Found in the Financials tab.' },
  { term: 'Feature Lift', definition: 'The increase in conditional share when a feature is included vs. excluded in a product concept. Measured in isolation.' },
  { term: 'Feature Scaling', definition: 'A dampener (0 to 1) applied to the total feature lift sum. Accounts for diminishing returns when bundling multiple features. Default: 0.5.' },
  { term: 'Months to Breakeven', definition: 'How many months of operation until cumulative contribution margins cover the fixed cost base. Shows "Now" if already profitable, "N/A" if contribution margin is negative.' },
  { term: 'Net Income per Unit', definition: 'Profit per sale after all variable costs: Price \u00d7 (1 \u2212 Deduction Rate) \u2212 S1 Acquisition \u2212 S2 Rep \u2212 Feature COGS. Does not include fixed costs.' },
  { term: 'Portfolio P&L', definition: 'Combined monthly profit/loss across all packages. Sum of all contribution margins minus fixed costs (applied once). The correct way to evaluate a multi-package strategy.' },
  { term: 'Required Close Rate', definition: 'What AlterMe\'s in-consultation close rate would need to be for the conjoint demand scenario to materialize through the actual funnel. Compare to the proven 30%.' },
  { term: 'Revenue per Prospect', definition: 'Price \u00d7 Concept-Level Choice Rate. Expected revenue from showing one concept to one respondent. Peaks at $1,499.' },
  { term: 'S1 (Stage 1)', definition: 'The lower-cost, digitally-sold lead generation offer (e.g., $99 DNA Kit) marketed to a mass audience. Each S2 package can have a different S1 offer.' },
  { term: 'S2 (Stage 2)', definition: 'The full product package sold via call center to S1 buyers. This is the revenue event. The conjoint study models demand for S2 packages.' },
  { term: 'S1 Investment ROI', definition: 'Net revenue generated per dollar spent on S1 acquisition. A value above 1.0x means S1 investment is generating positive returns.' },
  { term: 'Said vs. Did Gap', definition: 'The difference between stated feature importance (survey) and revealed preference (conjoint choices). Positive = Hidden Gem. Negative = Overhyped.' },
  { term: 'Scenario Projection', definition: 'A financial projection based on conjoint demand rates applied to operating assumptions. Not a sales forecast.' },
  { term: 'Split Demand', definition: 'Conservative demand estimate where total addressable demand per segment is shared proportionally across packages based on relative attractiveness.' },
  { term: 'Total Deduction Rate', definition: 'All-in percentage deducted from gross sale price: processing fees, refunds (14%), cancellations (3%), chargebacks. Default: 26.6%.' },
  { term: 'WASP', aka: 'Weighted Average Selling Price', definition: 'Total gross revenue divided by total units across all packages. The blended average price of the portfolio.' },
  { term: 'WTP', aka: 'Willingness to Pay', definition: 'The implied dollar value each feature adds, calculated by finding the price drop that matches the feature\'s demand lift.' },
];

// ─── Helper Components ─────────────────────────────────────────

function Callout({ type, children }: { type: 'warning' | 'info' | 'tip'; children: React.ReactNode }) {
  const cfg = {
    warning: { Icon: AlertTriangle, color: '#D97706', bg: 'bg-chart-amber/5', border: 'border-chart-amber/20' },
    info: { Icon: HelpCircle, color: '#0D9488', bg: 'bg-chart-teal/5', border: 'border-chart-teal/20' },
    tip: { Icon: Lightbulb, color: '#059669', bg: 'bg-emerald-500/5', border: 'border-emerald-500/20' },
  }[type];
  return (
    <div className={`flex items-start gap-3 px-5 py-4 rounded-xl border ${cfg.bg} ${cfg.border}`}>
      <cfg.Icon size={16} style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
      <div className="text-base text-secondary leading-relaxed">{children}</div>
    </div>
  );
}

function FormulaBlock({ label, formula }: { label: string; formula: string }) {
  return (
    <div className="rounded-xl px-5 py-4 my-3" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
      <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1.5">{label}</div>
      <div className="font-mono text-base text-primary">{formula}</div>
    </div>
  );
}

function Collapsible({ index, title, children }: { index: number; title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden transition-colors" style={{ backgroundColor: open ? 'var(--color-card)' : 'transparent', border: `1px solid var(--color-border)` }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-hover-bg transition-colors">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-chart-teal/10 text-chart-teal font-mono text-sm font-bold shrink-0">
          {String(index).padStart(2, '0')}
        </span>
        <span className="text-base font-semibold text-primary flex-1">{title}</span>
        <ChevronDown size={16} className={`text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 text-base text-secondary leading-relaxed space-y-3" style={{ marginLeft: '2.75rem' }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4 my-8">
      <div className="h-px flex-1" style={{ backgroundColor: 'var(--color-border)' }} />
      <span className="text-xs text-muted uppercase tracking-widest font-semibold shrink-0">{label}</span>
      <div className="h-px flex-1" style={{ backgroundColor: 'var(--color-border)' }} />
    </div>
  );
}

function TabGuideCard({ entry }: { entry: TabGuideEntry }) {
  const Icon = entry.icon;
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${entry.color}25`, backgroundColor: 'var(--color-card)' }}>
      {/* Colored top strip */}
      <div className="h-1" style={{ backgroundColor: entry.color }} />

      {/* Header */}
      <div className="px-6 py-5 flex items-center gap-3" style={{ backgroundColor: entry.color + '06' }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: entry.color + '12' }}>
          <Icon size={22} style={{ color: entry.color }} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-primary">{entry.name}</h3>
          {entry.subtitle && <span className="text-xs text-muted">{entry.subtitle}</span>}
        </div>
      </div>

      {/* Question */}
      <div className="mx-6 my-4 px-5 py-3.5 rounded-lg" style={{ borderLeft: `3px solid ${entry.color}`, backgroundColor: entry.color + '06' }}>
        <p className="text-base font-medium italic" style={{ color: entry.color }}>"{entry.question}"</p>
      </div>

      {/* Overview */}
      <div className="px-6 pb-2">
        <p className="text-base text-secondary leading-relaxed">{entry.overview}</p>
      </div>

      {/* Section Groups */}
      {entry.groups.map((group, gi) => (
        <div key={gi} className="px-6 pb-4">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-3 mt-2">{group.label}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {group.items.map((item, ii) => (
              <div key={ii} className="px-4 py-3.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div className="flex items-start gap-2.5">
                  <span className="text-xs font-mono font-bold shrink-0 mt-0.5" style={{ color: entry.color }}>
                    {String(ii + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-primary leading-snug">{item.name}</div>
                    <div className="text-xs text-muted mt-1 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Tips */}
      {entry.tips && entry.tips.length > 0 && (
        <div className="px-6 pb-5">
          <div className="flex items-start gap-3 px-5 py-3.5 rounded-lg" style={{ backgroundColor: entry.color + '08', border: `1px solid ${entry.color}15` }}>
            <Lightbulb size={16} style={{ color: entry.color }} className="shrink-0 mt-0.5" />
            <div className="text-sm text-secondary leading-relaxed space-y-1">
              {entry.tips.map((tip, i) => <p key={i}>{tip}</p>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Overview ─────────────────────────────────────────────

function OverviewContent() {
  return (
    <div className="space-y-8">
      {/* Intro */}
      <div className="space-y-4">
        <p className="text-base text-secondary leading-relaxed">
          The <strong className="text-primary">AlterMe Product Configurator</strong> helps leadership design, evaluate, and compare
          product portfolio strategies. It combines data from a <strong className="text-primary">Choice-Based Conjoint (CBC) study</strong> of{' '}
          <span className="font-mono">1,001</span> respondents with AlterMe's operational cost model to answer one strategic question:
        </p>
        <div className="px-5 py-4 rounded-xl border-l-3" style={{ borderLeftColor: '#0D9488', borderLeftWidth: '3px', backgroundColor: 'var(--color-surface)' }}>
          <p className="text-lg font-semibold text-primary italic">
            "What combination of packages, at what prices and feature bundles, gives us a profitable portfolio?"
          </p>
        </div>
        <p className="text-base text-secondary leading-relaxed">
          Configure <strong className="text-primary"><span className="font-mono">1</span> to <span className="font-mono">5</span> packages</strong>, each with its own
          S1 offer, S2 price, feature bundle, and cost structure. The app calculates portfolio-level P&L, per-package
          contribution margins, cannibalization estimates, and 12-month projections.
        </p>
        <p className="text-base text-secondary leading-relaxed">
          The conjoint study tested <span className="font-mono">5</span> price points (<span className="font-mono">$499</span> to{' '}
          <span className="font-mono">$1,899</span>), <span className="font-mono">7</span> product features, and{' '}
          <span className="font-mono">3</span> payment structures. Each respondent completed <span className="font-mono">15</span> choice
          tasks with <span className="font-mono">4</span> product concepts per task, plus a "none of these" option.
        </p>
      </div>

      <Callout type="warning">
        <strong>This is not a sales forecasting tool.</strong> The conjoint measures cold-market demand attractiveness among
        survey respondents who have never interacted with AlterMe. It shows relative preference across configurations, not
        absolute purchase predictions. All unit and P&L projections are labeled "(scenario)" for this reason.
      </Callout>

      <SectionDivider label="The Eight Tabs" />

      {/* 8 App Tab Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {APP_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <div key={tab.name} className="flex items-start gap-4 p-5 rounded-xl transition-all hover:shadow-md" style={{ backgroundColor: 'var(--color-card)', border: `1px solid var(--color-border)`, borderLeftWidth: '3px', borderLeftColor: tab.color }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: tab.color + '12' }}>
                <Icon size={18} style={{ color: tab.color }} />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-primary">{tab.name}</div>
                <div className="text-sm font-medium mt-0.5" style={{ color: tab.color }}>"{tab.question}"</div>
                <div className="text-sm text-muted mt-1.5 leading-relaxed">{tab.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Analyst */}
      <div className="flex items-start gap-3.5 px-5 py-4 rounded-xl" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
        <Sparkles size={18} className="text-chart-teal shrink-0 mt-0.5" />
        <div>
          <p className="text-base font-semibold text-primary mb-1">AI Analyst</p>
          <p className="text-sm text-secondary leading-relaxed">
            Available on every tab via the bottom nav (mobile) or the "AI Analyst" button (desktop).
            Ask questions about the data in natural language. The AI has full context of your current portfolio
            configuration, P&L metrics, and conjoint data.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Key Concepts ─────────────────────────────────────────

function ConceptsContent() {
  return (
    <div className="space-y-8">
      {/* Two Models */}
      <div>
        <h2 className="text-lg font-bold text-primary mb-2">Two Different Measurement Frameworks</h2>
        <p className="text-base text-muted mb-5">This tool and AlterMe's CFO model measure different things. The gap is not a bug.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Configurator */}
          <div className="rounded-xl p-5 space-y-3" style={{ backgroundColor: 'var(--color-card)', borderLeft: '3px solid #0D9488', border: '1px solid var(--color-border)', borderLeftWidth: '3px', borderLeftColor: '#0D9488' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-chart-teal/10">
                <Calculator size={16} className="text-chart-teal" />
              </div>
              <span className="text-base font-bold text-primary">The Configurator</span>
            </div>
            <p className="text-sm text-secondary leading-relaxed">
              Applies <strong>conjoint cold-market demand rates</strong> to S1 lead volume.
              These rates come from <span className="font-mono">1,001</span> survey respondents who have never heard of AlterMe.
            </p>
            <FormulaBlock label="Formula" formula="S2 Units = S1 Leads × Adjusted Choice Rate" />
            <div className="text-sm text-muted font-mono">
              Example at $1,899: 5,300 × 11.85% = 628 units
            </div>
          </div>

          {/* CFO Model */}
          <div className="rounded-xl p-5 space-y-3" style={{ backgroundColor: 'var(--color-card)', borderLeft: '3px solid #2563EB', border: '1px solid var(--color-border)', borderLeftWidth: '3px', borderLeftColor: '#2563EB' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-chart-blue/10">
                <LayoutDashboard size={16} className="text-chart-blue" />
              </div>
              <span className="text-base font-bold text-primary">The CFO's Model</span>
            </div>
            <p className="text-sm text-secondary leading-relaxed">
              Models the <strong>actual operational funnel</strong>: S1 purchase &rarr; kit return (85%) &rarr;
              consultation (85%) &rarr; close (20-30%). Proven rates from real sales data.
            </p>
            <FormulaBlock label="Formula" formula="S1 × Kit Return × Consult Rate × Close Rate" />
            <div className="text-sm text-muted font-mono">
              Example: 5,300 × 85% × 85% × 28% = 1,072 units
            </div>
          </div>
        </div>
      </div>

      <SectionDivider label="Connecting the Two" />

      {/* The Bridge */}
      <div className="rounded-xl p-6 space-y-4" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2.5">
          <ArrowRight size={18} className="text-chart-teal" />
          <h3 className="text-lg font-bold text-primary">The Bridge: Required Close Rate</h3>
        </div>
        <p className="text-base text-secondary leading-relaxed">
          To connect the two frameworks, the app shows the <strong className="text-primary">Required Close Rate</strong> &mdash; what
          AlterMe's in-consultation close rate would need to be for the conjoint demand scenario to materialize through the
          actual funnel.
        </p>
        <FormulaBlock label="Required Close Rate" formula="Adjusted Choice Rate ÷ (Kit Return Rate × Consultation Rate)" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex items-center gap-3 px-4 py-3.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            <span className="w-4 h-4 rounded-full bg-metric-positive shrink-0" />
            <div>
              <div className="text-sm font-semibold text-primary">&le; 28%</div>
              <div className="text-xs text-muted">Conservative &mdash; well below proven rate</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            <span className="w-4 h-4 rounded-full bg-chart-amber shrink-0" />
            <div>
              <div className="text-sm font-semibold text-primary">28&ndash;35%</div>
              <div className="text-xs text-muted">Stretch &mdash; near the limit of proven</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            <span className="w-4 h-4 rounded-full bg-metric-negative shrink-0" />
            <div>
              <div className="text-sm font-semibold text-primary">&gt; 35%</div>
              <div className="text-xs text-muted">Aggressive &mdash; beyond proven performance</div>
            </div>
          </div>
        </div>
      </div>

      <SectionDivider label="Analysis Modes" />

      {/* Portfolio vs Single */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-primary">Portfolio vs. Single-Product Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl space-y-2" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="text-sm text-chart-teal font-bold uppercase tracking-wider">Portfolio Analysis</div>
            <p className="text-sm text-secondary leading-relaxed">
              Fixed costs applied <strong>once</strong> across all packages. Shows combined P&L with contribution margins
              per package. The correct way to evaluate a multi-tier strategy.
            </p>
          </div>
          <div className="p-5 rounded-xl space-y-2" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="text-sm text-chart-blue font-bold uppercase tracking-wider">Single-Product Analysis</div>
            <p className="text-sm text-secondary leading-relaxed">
              Full fixed costs applied to each price point independently. Useful for comparing relative attractiveness,
              not for evaluating a multi-package strategy.
            </p>
          </div>
        </div>
      </div>

      {/* Demand Modes */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-primary">Demand Modes: Ceiling vs. Split</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl space-y-2" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="text-sm font-bold uppercase tracking-wider" style={{ color: '#059669' }}>Ceiling (Optimistic)</div>
            <p className="text-sm text-secondary leading-relaxed">
              Each package captures its full conjoint demand independently. No cannibalization. Total units may exceed
              realistic estimates.
            </p>
          </div>
          <div className="p-5 rounded-xl space-y-2" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="text-sm font-bold uppercase tracking-wider" style={{ color: '#D97706' }}>Split (Conservative)</div>
            <p className="text-sm text-secondary leading-relaxed">
              Per-segment demand is allocated proportionally across packages based on relative attractiveness. Total units
              never exceed the ceiling. An additional <strong>Cannibalization Rate</strong> slider models trade-down.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Tab Guide ────────────────────────────────────────────

function TabGuideContent() {
  return (
    <div className="space-y-6">
      <p className="text-base text-muted">
        Detailed breakdown of every tab in the configurator. Each section is numbered so you can find it quickly.
      </p>
      {TAB_GUIDE.map((entry, i) => (
        <TabGuideCard key={i} entry={entry} />
      ))}
    </div>
  );
}

// ─── Tab: Methodology ──────────────────────────────────────────

function MethodologyContent() {
  return (
    <div className="space-y-3">
      <p className="text-base text-muted mb-4">For analysts and the CFO. Click each section to expand the details.</p>

      <Collapsible index={1} title="Adjusted Choice Rate Calculation">
        <FormulaBlock label="Adjusted Choice Rate" formula="Base Rate × (1 + Total Feature Lift × Feature Scaling)" />
        <p>The base rate comes from the conjoint's concept-level choice rates at each price point. Feature lift is the sum of individual feature lifts for enabled features. Feature Scaling (default <span className="font-mono">0.5</span>) dampens the total lift to account for diminishing returns when bundling multiple features.</p>
        <p>Example at $1,499, all 7 features, 0.5 scaling: <span className="font-mono">17.20% × (1 + 67.8% × 0.5) = 23.0%</span></p>
      </Collapsible>

      <Collapsible index={2} title="Portfolio P&L Model">
        <p>Fixed costs are applied <strong>once</strong> across the entire portfolio, not per-package. Each package shows its <strong>contribution margin</strong> (net revenue minus variable costs).</p>
        <FormulaBlock label="Contribution Margin" formula="(S2 Units × Price × (1 − Deduction Rate)) − S1 Acquisition − S2 Rep − Feature COGS" />
        <FormulaBlock label="Portfolio P&L" formula="\u03A3(Package Contributions) − Monthly Fixed Costs" />
        <p><strong>Per-package cost overrides:</strong> Each package can have its own S1 cost, S2 close rate, deduction rate, S2 rep cost, kit return rate, and consult rate. When not overridden, the global default is used.</p>
      </Collapsible>

      <Collapsible index={3} title="Demand Modes: Ceiling vs. Split">
        <p><strong>Ceiling (optimistic):</strong> Each package captures its full conjoint demand independently, assuming no cannibalization.</p>
        <p><strong>Split (conservative):</strong> Per-segment demand is allocated proportionally across packages based on relative attractiveness. Total units never exceed the ceiling.</p>
        <p><strong>Cannibalization:</strong> An additional adjustment where a fraction (<span className="font-mono">0&ndash;50%</span>) of higher-priced package demand trades down to cheaper packages. Applied on top of the split model.</p>
      </Collapsible>

      <Collapsible index={4} title="S1/S2 Funnel Model">
        <p>AlterMe uses a two-stage sales funnel. <strong>S1</strong> is a lower-cost, digitally-sold lead generation offer (e.g., $99 DNA Kit). <strong>S2</strong> is the full product package sold via call center to S1 buyers.</p>
        <p>Each S2 package can have a <strong>different S1 offer</strong> with different costs and close rates.</p>
        <FormulaBlock label="Funnel Flow" formula="S1 Purchase → Kit Return (85%) → Consultation (85%) → S2 Close (varies)" />
        <p>S1 acquisition cost per S2 sale = S1 Cost per Kit &divide; S2 Close Rate.</p>
      </Collapsible>

      <Collapsible index={5} title="Feature Lift and Scaling">
        <p>Each feature's lift is measured in isolation: the difference in conditional share when included vs. excluded. When multiple features are bundled, their combined effect is less than the sum of individual lifts.</p>
        <p>The Feature Scaling parameter (<span className="font-mono">0</span> to <span className="font-mono">1</span>) controls this dampening. Default <span className="font-mono">0.5</span> is a conservative middle ground.</p>
      </Collapsible>

      <Collapsible index={6} title="Segment Clustering and Assignment">
        <p>Three segments were identified using k-means clustering on part-worth utilities. In the Segments tab, individual respondents are assigned to segments based on behavioral similarity (none rate and price distribution patterns).</p>
        <p>Segment-level feature lifts were not measured &mdash; overall feature lifts are applied uniformly across segments.</p>
      </Collapsible>

      <Collapsible index={7} title="Customer LTV Model">
        <p>The LTV calculator projects per-customer value over 24 months using: monthly retention rate, monthly recurring revenue, and a one-time upsell opportunity at month 3. This determines whether loss-leader S1 tiers can be justified through retention and upsells.</p>
        <FormulaBlock label="Payback" formula="Month where cumulative revenue exceeds acquisition cost" />
      </Collapsible>

      <Collapsible index={8} title="Willingness to Pay (WTP) Method">
        <p>WTP is computed using the demand-equivalence method. For each feature at a reference price: find the price drop that achieves the same demand without the feature. The difference is the feature's implied WTP in dollars.</p>
      </Collapsible>

      <Collapsible index={9} title="Data Provenance">
        <p><strong>Conjoint Study:</strong> Choice-Based Conjoint (CBC), February 2026. n=<span className="font-mono">1,001</span> respondents. <span className="font-mono">15</span> tasks/respondent, <span className="font-mono">4</span> concepts/task plus "none." <span className="font-mono">5</span> price levels, <span className="font-mono">7</span> binary features, <span className="font-mono">3</span> payment options.</p>
        <p><strong>Demographic Data:</strong> <span className="font-mono">33+</span> fields per respondent including gender, age, income, race, education, employment, region, health goals, monthly spend, feature importance/ranking, attitudes (5 statements), purchase factor ranking (7 factors), brand consideration (12 brands), and wearable device ownership (11 devices).</p>
        <p><strong>Operating Assumptions:</strong> S1 leads, kit return rate, consultation rate, close rate, fixed costs, variable costs, and deduction rate come from AlterMe's CFO Assumption Calculator (February 2026). These are user-adjustable inputs.</p>
      </Collapsible>

      <Collapsible index={10} title="Study Limitations">
        <ol className="list-decimal list-inside space-y-2 text-base">
          <li><strong>Hypothetical bias.</strong> Survey respondents tend to overstate willingness to pay.</li>
          <li><strong>No interaction model.</strong> Feature lifts are measured in isolation; true bundle value is unknown.</li>
          <li><strong>Five price points only.</strong> Demand between tested points is interpolated, not measured.</li>
          <li><strong>Static snapshot.</strong> Reflects preferences in February 2026.</li>
          <li><strong>No competitive context.</strong> Concepts were evaluated against "none," not named competitors.</li>
          <li><strong>Sample &ne; customer base.</strong> Respondents are from a general panel, not AlterMe's existing funnel.</li>
        </ol>
      </Collapsible>
    </div>
  );
}

// ─── Tab: Reference ────────────────────────────────────────────

function ReferenceContent() {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return GLOSSARY;
    const q = search.toLowerCase();
    return GLOSSARY.filter(g =>
      g.term.toLowerCase().includes(q) ||
      (g.aka?.toLowerCase().includes(q)) ||
      g.definition.toLowerCase().includes(q)
    );
  }, [search]);

  function highlight(text: string): React.ReactNode {
    if (!search.trim()) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} className="bg-chart-teal/20 text-primary rounded-sm px-0.5">{part}</mark>
        : part
    );
  }

  return (
    <div className="space-y-8">
      {/* Glossary */}
      <div>
        <h2 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
          <BookOpen size={20} className="text-chart-teal" />
          Glossary
        </h2>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search terms..."
            className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-border text-primary placeholder:text-muted transition-colors focus:outline-none focus:border-chart-teal/40 focus:ring-1 focus:ring-chart-teal/20"
            style={{ backgroundColor: 'var(--color-input-bg)' }}
          />
          {search && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-muted">
              <span className="font-mono">{filtered.length}</span> of <span className="font-mono">{GLOSSARY.length}</span>
            </span>
          )}
        </div>

        {/* Terms */}
        <div className="space-y-0 rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
          {filtered.map((item, i) => (
            <div key={item.term} className="px-5 py-4 transition-colors hover:bg-hover-bg" style={i < filtered.length - 1 ? { borderBottom: '1px solid var(--color-border)' } : undefined}>
              <dt className="text-base font-semibold text-primary">
                {highlight(item.term)}
                {item.aka && <span className="text-sm text-muted font-normal ml-2">({highlight(item.aka)})</span>}
              </dt>
              <dd className="text-sm text-secondary mt-1 leading-relaxed">{highlight(item.definition)}</dd>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-5 py-8 text-center text-base text-muted">
              No terms match "{search}"
            </div>
          )}
        </div>
      </div>

      <SectionDivider label="Access" />

      {/* Access & Auth */}
      <div className="rounded-xl p-6 space-y-4" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <Lock size={18} className="text-chart-purple" />
          <h3 className="text-lg font-bold text-primary">Access & Authentication</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">Sign In</div>
            <p className="text-sm text-secondary leading-relaxed">
              Magic link authentication. Enter your email and click "Send sign-in link." The link expires in <span className="font-mono">15</span> minutes. Sessions last <span className="font-mono">30</span> days.
            </p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">Who Can Access</div>
            <p className="text-sm text-secondary leading-relaxed">
              Only @alterme.com email addresses and the super admin. Each user gets a private workspace with auto-saved configurations.
            </p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">Your Workspace</div>
            <p className="text-sm text-secondary leading-relaxed">
              Package configs, scenarios, assumptions, and chat history persist across sessions automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────

export function HelpGuidePage({ onClose }: HelpGuidePageProps) {
  const [activeTab, setActiveTab] = useState<GuideTab>('overview');

  const handleTabChange = (tab: GuideTab) => {
    setActiveTab(tab);
    document.getElementById('guide-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <OverviewContent />;
      case 'concepts': return <ConceptsContent />;
      case 'tabs': return <TabGuideContent />;
      case 'methodology': return <MethodologyContent />;
      case 'reference': return <ReferenceContent />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-6 pb-24 md:pb-8">
      {/* Hero */}
      <div className="mb-6">
        <button onClick={onClose} className="flex items-center gap-1.5 text-sm text-chart-teal hover:opacity-70 transition-opacity mb-5">
          <ArrowLeft size={14} /> Back to Configurator
        </button>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-chart-teal/10 shrink-0">
            <BookOpen size={26} className="text-chart-teal" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-primary tracking-tight">Product Configurator Guide</h1>
            <p className="text-base text-muted mt-1">
              A comprehensive reference for understanding the AlterMe strategy tool, its data, and methodology.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div className="flex gap-0 min-w-max">
          {GUIDE_NAV.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'text-chart-teal border-chart-teal'
                    : 'text-muted border-transparent hover:text-secondary hover:border-border'
                }`}
              >
                <Icon size={15} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div id="guide-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="text-center py-8 mt-8" style={{ borderTop: '1px solid var(--color-border)' }}>
        <p className="text-sm text-muted">AlterMe Product Configurator &middot; CBC Study, February 2026 &middot; n=<span className="font-mono">1,001</span></p>
        <button onClick={onClose} className="mt-4 px-5 py-2.5 text-sm font-medium rounded-xl bg-chart-teal text-white hover:opacity-90 transition-opacity">
          Back to Configurator
        </button>
        <div className="mt-6 flex items-center justify-center gap-2">
          <img src="/angry-gods-logo.png" alt="Angry Gods" className="w-5 h-5 rounded" />
          <p className="text-xs text-muted">
            Fielded, designed and developed by{' '}
            <a href="https://www.angrygods.com" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary transition-colors underline underline-offset-2">
              Angry Gods
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
